import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip, Typography, Chip, Dialog,
    DialogTitle, DialogContent, DialogActions, TablePagination, List, ListItem,
    ListItemIcon, ListItemText, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    getPaymentRequests, cancelPaymentRequest, resendPaymentRequestEmail,
    downloadPaymentRequestDocument, deletePaymentRequestDocument,
    uploadPaymentRequestDocuments,
} from '../../api/api';
import { useToast } from '../../components/ToastProvider/ToastProvider';
import CreatePaymentRequestForm from '../../components/CreatePaymentRequestForm/CreatePaymentRequestForm';
import { DashboardContainer, FilterRow } from './PaymentRequestDashboard.styles';

const STATUS_COLORS = {
    PENDING: '#ed6c02',
    COMPLETED: '#2e7d32',
    FAILED: '#d32f2f',
    EXPIRED: '#757575',
    CANCELLED: '#9e9e9e',
};

const STATUS_OPTIONS = ['', 'PENDING', 'COMPLETED', 'FAILED', 'EXPIRED', 'CANCELLED'];

export default function PaymentRequestDashboard() {
    const { showToast } = useToast();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [uploadingDocs, setUploadingDocs] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            if (search) params.append('search', search);
            params.append('page', String(page + 1));
            params.append('page_size', String(pageSize));
            const data = await getPaymentRequests(params.toString());
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            showToast(e.message || 'Failed to load payment requests', 'error');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search, page, pageSize, showToast]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCancel = async (id) => {
        if (!window.confirm('Cancel this payment request?')) return;
        try {
            await cancelPaymentRequest(id);
            showToast('Payment request cancelled', 'success');
            fetchData();
        } catch (e) {
            showToast(e.message || 'Failed to cancel', 'error');
        }
    };

    const handleResend = async (id) => {
        try {
            await resendPaymentRequestEmail(id);
            showToast('Email resent successfully', 'success');
            fetchData();
        } catch (e) {
            showToast(e.message || 'Failed to resend email', 'error');
        }
    };

    const handleCopyLink = (url) => {
        navigator.clipboard.writeText(url);
        showToast('Payment link copied!', 'success');
    };

    const handleViewDetail = (row) => {
        setSelected(row);
        setDetailOpen(true);
    };

    const handleDocDownload = async (prId, doc) => {
        try {
            await downloadPaymentRequestDocument(prId, doc.id, doc.file_name);
        } catch (e) {
            showToast('Download failed', 'error');
        }
    };

    const handleDocDelete = async (prId, docId) => {
        if (!window.confirm('Delete this document?')) return;
        try {
            await deletePaymentRequestDocument(prId, docId);
            showToast('Document deleted', 'success');
            // Refresh the selected item
            const updated = rows.find(r => r.id === prId);
            if (updated) {
                setSelected({ ...updated, documents: updated.documents.filter(d => d.id !== docId) });
            }
            fetchData();
        } catch (e) {
            showToast('Delete failed', 'error');
        }
    };

    const handleDocUpload = async (prId, files) => {
        if (!files || files.length === 0) return;
        setUploadingDocs(true);
        try {
            const formData = new FormData();
            Array.from(files).forEach(f => formData.append('files', f));
            await uploadPaymentRequestDocuments(prId, formData);
            showToast('Documents uploaded', 'success');
            fetchData();
        } catch (e) {
            showToast(e.message || 'Upload failed', 'error');
        } finally {
            setUploadingDocs(false);
        }
    };

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Indian/Mauritius' }) : '-';
    const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Indian/Mauritius' }) : '-';
    const fmtAmount = (amt, cur) => `${cur} ${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    return (
        <DashboardContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={600}>Payment Requests</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreate(true)}>
                    Create Payment Request
                </Button>
            </Box>

            <FilterRow>
                <TextField
                    size="small" label="Search" value={search}
                    onChange={e => { setSearch(e.target.value); setPage(0); }}
                    sx={{ minWidth: 220 }}
                />
                <TextField
                    select size="small" label="Status" value={statusFilter}
                    onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                    sx={{ minWidth: 160 }}
                >
                    {STATUS_OPTIONS.map(s => (
                        <MenuItem key={s} value={s}>{s || 'All Statuses'}</MenuItem>
                    ))}
                </TextField>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
            </FilterRow>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Reference</strong></TableCell>
                            <TableCell><strong>Customer</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Amount</strong></TableCell>
                            <TableCell><strong>Purpose</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Expiry</strong></TableCell>
                            <TableCell><strong>Created</strong></TableCell>
                            <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={9} align="center"><CircularProgress size={28} /></TableCell></TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={9} align="center">No payment requests found</TableCell></TableRow>
                        ) : rows.map(row => (
                            <TableRow key={row.id} hover>
                                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.reference}</TableCell>
                                <TableCell>{row.customer_name}</TableCell>
                                <TableCell>{row.customer_email}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{fmtAmount(row.amount, row.currency)}</TableCell>
                                <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.purpose}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.status}
                                        size="small"
                                        sx={{ fontWeight: 600, bgcolor: STATUS_COLORS[row.status] || '#999', color: '#fff' }}
                                    />
                                </TableCell>
                                <TableCell>{fmtDate(row.expires_at)}</TableCell>
                                <TableCell>{fmtDate(row.created_at)}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="View Details"><IconButton size="small" onClick={() => handleViewDetail(row)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Copy Link"><IconButton size="small" onClick={() => handleCopyLink(row.link_url)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                                    {row.status === 'PENDING' && (
                                        <>
                                            <Tooltip title="Resend Email"><IconButton size="small" onClick={() => handleResend(row.id)}><EmailIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => handleCancel(row.id)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div" count={total} page={page} rowsPerPage={pageSize}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </TableContainer>

            {/* Create Dialog */}
            <CreatePaymentRequestForm
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onSuccess={() => { setShowCreate(false); fetchData(); }}
            />

            {/* Detail Dialog */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Payment Request Details</DialogTitle>
                {selected && (
                    <DialogContent dividers>
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
                            <Box><Typography variant="caption" color="textSecondary">Reference</Typography><Typography fontWeight={600}>{selected.reference}</Typography></Box>
                            <Box><Typography variant="caption" color="textSecondary">Status</Typography><br /><Chip label={selected.status} size="small" sx={{ fontWeight: 600, bgcolor: STATUS_COLORS[selected.status], color: '#fff' }} /></Box>
                            <Box><Typography variant="caption" color="textSecondary">Customer Name</Typography><Typography>{selected.customer_name}</Typography></Box>
                            <Box><Typography variant="caption" color="textSecondary">Customer Email</Typography><Typography>{selected.customer_email}</Typography></Box>
                            {selected.customer_phone && <Box><Typography variant="caption" color="textSecondary">Phone</Typography><Typography>{selected.customer_phone}</Typography></Box>}
                            <Box><Typography variant="caption" color="textSecondary">Amount</Typography><Typography variant="h6" fontWeight={700} color="primary">{fmtAmount(selected.amount, selected.currency)}</Typography></Box>
                            <Box><Typography variant="caption" color="textSecondary">Purpose</Typography><Typography>{selected.purpose}</Typography></Box>
                            {selected.description && <Box gridColumn="1 / -1"><Typography variant="caption" color="textSecondary">Description</Typography><Typography>{selected.description}</Typography></Box>}
                            <Box><Typography variant="caption" color="textSecondary">Expiry Date (UTC +4)</Typography><Typography>{fmtDateTime(selected.expires_at)}</Typography></Box>
                            <Box><Typography variant="caption" color="textSecondary">Created</Typography><Typography>{fmtDateTime(selected.created_at)}</Typography></Box>
                            {selected.paid_at && <Box><Typography variant="caption" color="textSecondary">Paid At</Typography><Typography>{fmtDateTime(selected.paid_at)}</Typography></Box>}
                            {selected.transaction_reference && <Box><Typography variant="caption" color="textSecondary">Transaction Ref</Typography><Typography>{selected.transaction_reference}</Typography></Box>}
                            <Box gridColumn="1 / -1">
                                <Typography variant="caption" color="textSecondary">Payment Link</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>{selected.link_url}</Typography>
                                    <IconButton size="small" onClick={() => handleCopyLink(selected.link_url)}><ContentCopyIcon fontSize="small" /></IconButton>
                                </Box>
                            </Box>
                        </Box>

                        {/* Documents section */}
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>Documents</Typography>
                        {selected.documents && selected.documents.length > 0 ? (
                            <List dense>
                                {selected.documents.map(doc => (
                                    <ListItem key={doc.id} secondaryAction={
                                        <Box>
                                            <Tooltip title="Download"><IconButton edge="end" size="small" onClick={() => handleDocDownload(selected.id, doc)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                            {selected.status === 'PENDING' && (
                                                <Tooltip title="Delete"><IconButton edge="end" size="small" color="error" onClick={() => handleDocDelete(selected.id, doc.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                            )}
                                        </Box>
                                    }>
                                        <ListItemIcon><DescriptionIcon /></ListItemIcon>
                                        <ListItemText
                                            primary={doc.file_name}
                                            secondary={doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="textSecondary">No documents attached</Typography>
                        )}

                        {selected.status === 'PENDING' && (
                            <Box mt={2}>
                                <Button
                                    variant="outlined" startIcon={uploadingDocs ? <CircularProgress size={16} /> : <UploadFileIcon />}
                                    component="label" disabled={uploadingDocs}
                                >
                                    Upload Documents
                                    <input type="file" hidden multiple onChange={e => handleDocUpload(selected.id, e.target.files)} />
                                </Button>
                            </Box>
                        )}
                    </DialogContent>
                )}
                <DialogActions>
                    {selected && selected.status === 'PENDING' && (
                        <Button startIcon={<EmailIcon />} onClick={() => { handleResend(selected.id); setDetailOpen(false); }}>
                            Resend Email
                        </Button>
                    )}
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </DashboardContainer>
    );
}
